import { PRIORTY_CURRENT, PRIORTY_PREVIOUS } from '../../HatMetrics';

import { Grid } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { hatMetrics } from '../../HatMetrics';
import { useHatQuery } from '../../HatPriorityQueryQueue';
import { sumIfAllNotNull } from '../../HatUtils';
import { useHatDbContext } from '../../context/HatDbContext';
import { useHatDbDataContext } from '../../context/HatDbDataContext';
import { HatDbVisualChannelAttributionPieChartCard } from '../widgets/HatDbVisualChannelAttributionPieChartCard';
import { HatDbVisualChannelAttributionTable } from '../widgets/HatDbVisualChannelAttributionTable';

type HatMetricChannelAttributionCount = {
    count: number | null;
    adInfluencedCount: number | null;
};

type HatMetricChannelAttributionRow = {
    sourceCategory: string;
} & HatMetricChannelAttributionCount;

export type HatMetricChannelAttributionData = HatMetricChannelAttributionRow[] & {
    total?: HatMetricChannelAttributionCount;
};

/**
 * Adds source categories that do not exist in sourceData, but do exist in compareData.
 * @param sourceData Data to receive missing source categories.
 * @param compareData Data to compare.
 */
function addMissingSourceCategories(
    sourceData: HatMetricChannelAttributionData,
    compareData: HatMetricChannelAttributionData
) {
    for (const check of compareData) {
        if (!sourceData.find((v) => v.sourceCategory === check.sourceCategory)) {
            sourceData.push({
                sourceCategory: check.sourceCategory,
                count: 0,
                adInfluencedCount: 0,
            });
        }
    }
}

/**
 * Sum the counts for each channel in the result data.
 */
function sumTotals(data: HatMetricChannelAttributionData) {
    return {
        count: sumIfAllNotNull(...data.map((v) => v.count)),
        adInfluencedCount: sumIfAllNotNull(...data.map((v) => v.adInfluencedCount)),
    };
}

/**
 * Default list of empty channels if there is no data at all.
 * This is to prevent "nothing" being displayed.
 */
const emptyData: HatMetricChannelAttributionRow[] = ['Amazon', 'DV360', 'Direct', 'Other'].map((sourceCategory) => ({
    sourceCategory,
    count: 0,
    adInfluencedCount: 0,
}));

export default function HatDbSectionChannelAttribution(props: { dataContextKey: string }) {
    const hatDbContext = useHatDbContext();
    const hatDbDataContext = useHatDbDataContext();

    // Transform the result to something easier to use.
    function selectFn(data: any): HatMetricChannelAttributionRow[] {
        return data.map((r: any) => ({
            sourceCategory: r.source_category,
            count: r.session_count,
            adInfluencedCount: r.ad_influenced_session_count,
        }));
    }

    // Get the data from the queries.
    const currentCriteria = hatDbContext.getCurrentRangeCriteria();
    const currentQuery = useHatQuery(
        {
            ...hatMetrics.channelAttribution,
            ...currentCriteria,
            priority: PRIORTY_CURRENT,
            select: selectFn,
        },
        !!currentCriteria
    );

    const previousCriteria = hatDbContext.getPreviousRangeCriteria();
    const previousQuery = useHatQuery(
        {
            ...hatMetrics.channelAttribution,
            ...previousCriteria!,
            priority: PRIORTY_PREVIOUS,
            select: selectFn,
        },
        !!previousCriteria
    );

    const { currentData, previousData } = useMemo(() => {
        // Create empty objects to hold the data.
        const currentData: HatMetricChannelAttributionData = [];
        currentData.total = {
            count: null,
            adInfluencedCount: null,
        };
        const previousData = structuredClone(currentData);

        if (!currentQuery.isPending && !currentQuery.isError) {
            const results = currentQuery.data as HatMetricChannelAttributionRow[];
            if (results.length) {
                currentData.push(...results);
                currentData.total = sumTotals(results);
            } else {
                currentData.push(...structuredClone(emptyData));
                currentData.total = {
                    count: 0,
                    adInfluencedCount: 0,
                };
            }
        }

        if (!previousQuery.isPending && !previousQuery.isError) {
            const results = previousQuery.data as HatMetricChannelAttributionRow[];
            if (results.length) {
                previousData.push(...results);
                previousData.total = sumTotals(results);
            } else {
                previousData.push(...structuredClone(emptyData));
                previousData.total = {
                    count: 0,
                    adInfluencedCount: 0,
                };
            }
        }

        if (!currentQuery.isPending && !previousQuery.isPending) {
            // Add entries for any source categories that exist in only one interval.
            addMissingSourceCategories(currentData, previousData);
            addMissingSourceCategories(previousData, currentData);
        }

        return {
            currentData,
            previousData,
        };
    }, [
        currentQuery.isPending,
        currentQuery.isError,
        currentQuery.data,
        previousQuery.isPending,
        previousQuery.isError,
        previousQuery.data,
    ]);

    useEffect(() => {
        // Publish the totals on the dashboard data context.
        hatDbDataContext?.setData(props.dataContextKey, 'current', currentData);
        hatDbDataContext?.setData(props.dataContextKey, 'previous', previousData);
    }, [currentData, previousData, hatDbDataContext, props.dataContextKey]);

    return (
        <Grid container spacing={2}>
            <Grid item md={6} xs={12}>
                <HatDbVisualChannelAttributionPieChartCard
                    isPending={currentQuery.isPending}
                    currentData={currentData}
                />
            </Grid>

            <Grid item md={6} xs={12}>
                <HatDbVisualChannelAttributionTable currentData={currentData} previousData={previousData} />
            </Grid>
        </Grid>
    );
}
